// ─────────────────────────────────────────────────────────────────────────────
// Lignes employé de la grille salariale — fonctions pures, importables côté
// client (aucun Firestore). Chaque employé devient une LIGNE dans la grille de
// sa classe, avec des marqueurs par période à chaque échelon :
//  - passé : positions des années de grille antérieures, fusion de
//    grid_positions (saisie manuelle, prioritaire) et d'echelon_history
//    (dérivé : to_echelon à effective_year, dans la classe COURANTE — seule
//    classe connue de cet historique) ;
//  - réel : classe/échelon courants (pulse_compensation) ;
//  - scénario : changement simulé/persisté (pulse_salary_scenarios).
// Un employé avec un passé (ou un scénario) dans une AUTRE classe produit une
// ligne supplémentaire dans cette classe (isCurrentClass: false), dépliable
// via le « + » à côté de son nom.
// ─────────────────────────────────────────────────────────────────────────────

import type { EchelonChange, GridPosition, ScenarioChange } from '../types/firestore'

export type PositionedMember = {
  email: string
  name: string
  class_id: string | null
  echelon: number
  // Niveau persisté (pulse_compensation.current_niveau) — optionnel,
  // rétrocompatible. Affiché en badge sur la ligne de la classe courante.
  niveau?: number | null
  // Membre actif. Absent ⇒ actif (rétrocompatible). false ⇒ ombragé dans les
  // grilles en vue passé/budget.
  active?: boolean
  // Hors grille : l'échelon courant est périmé — le marqueur « Actuel » se pose
  // sur l'ÉCHELON DE RÉFÉRENCE (plafond du niveau / plancher, dérivé du taux
  // réel côté serveur via offGridPosition) et la ligne n'est pas déplaçable.
  offGrid?: boolean
  offGridReferenceEchelon?: number | null
}

export type PeriodMarker =
  | { kind: 'past'; year: number; source: 'manual' | 'derived' }
  | { kind: 'current'; offGrid?: boolean }
  | { kind: 'scenario' }

export type EmployeeGridLine = {
  // Email pour un membre réel, identifiant local pour un fictif.
  id: string
  name: string
  email: string | null
  virtual: boolean
  class_id: string // la classe de CETTE ligne
  // false ⇒ ligne d'une autre classe que la classe courante de l'employé
  // (passé ou scénario inter-classe) — repliée par défaut dans la grille.
  isCurrentClass: boolean
  // true si la ligne porte un marqueur scénario (toujours affichée).
  hasScenario: boolean
  // Niveau persisté de l'employé — uniquement sur sa ligne de classe courante
  // (null pour les lignes passées/inter-classe et les fictifs).
  niveau: number | null
  // Membre actif (true pour les fictifs). false ⇒ ligne ombragée en passé/budget.
  active: boolean
  // Hors grille (ligne de classe courante) : non déplaçable, marqueur « HG » sur
  // l'échelon de référence.
  offGrid: boolean
  markers: Record<number /* echelon */, PeriodMarker[]>
}

// Changement de scénario côté client : un id local distingue les fictifs
// (le ScenarioChange persisté n'en a pas besoin, l'email suffit pour les réels).
export type ScenarioChangeInput = ScenarioChange & { local_id?: string }

function addMarker(
  byClass: Map<string, Map<number, PeriodMarker[]>>,
  classId: string,
  echelon: number,
  marker: PeriodMarker,
) {
  let cls = byClass.get(classId)
  if (!cls) {
    cls = new Map()
    byClass.set(classId, cls)
  }
  const cell = cls.get(echelon)
  if (cell) cell.push(marker)
  else cls.set(echelon, [marker])
}

// Positions passées d'un membre, une par année : manuel (grid_positions)
// prioritaire sur dérivé (echelon_history, classe courante).
function pastByYear(
  member: PositionedMember,
  manual: GridPosition[],
  history: EchelonChange[],
  currentYear: number,
): Map<number, { class_id: string; echelon: number; source: 'manual' | 'derived' }> {
  const byYear = new Map<number, { class_id: string; echelon: number; source: 'manual' | 'derived' }>()
  if (member.class_id) {
    for (const c of history) {
      if (c.effective_year < currentYear) {
        byYear.set(c.effective_year, {
          class_id: member.class_id,
          echelon: c.to_echelon,
          source: 'derived',
        })
      }
    }
  }
  for (const p of manual) {
    if (p.year < currentYear) {
      byYear.set(p.year, { class_id: p.class_id, echelon: p.echelon, source: 'manual' })
    }
  }
  return byYear
}

// Fusionne réel + passé + scénario en lignes par classe, prêtes pour la grille.
export function buildEmployeeLines(input: {
  members: PositionedMember[]
  pastPositions: Record<string /* email */, GridPosition[]>
  echelonHistory: Record<string /* email */, EchelonChange[]>
  scenarioChanges: ScenarioChangeInput[]
  currentYear: number
}): Record<string /* class_id */, EmployeeGridLine[]> {
  const { members, pastPositions, echelonHistory, scenarioChanges, currentYear } = input

  const result: Record<string, EmployeeGridLine[]> = {}
  const push = (line: EmployeeGridLine) => {
    ;(result[line.class_id] ??= []).push(line)
  }

  const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name))

  for (const m of sortedMembers) {
    const byClass = new Map<string, Map<number, PeriodMarker[]>>()

    // Passé (manuel prioritaire sur dérivé), trié par année pour l'affichage.
    const past = [...pastByYear(
      m,
      pastPositions[m.email] ?? [],
      echelonHistory[m.email] ?? [],
      currentYear,
    ).entries()].sort(([a], [b]) => a - b)
    for (const [year, p] of past) {
      addMarker(byClass, p.class_id, p.echelon, { kind: 'past', year, source: p.source })
    }

    // Réel. Hors grille : le marqueur se pose sur l'échelon de RÉFÉRENCE
    // (plafond/plancher du niveau) — l'échelon stocké est périmé.
    if (m.class_id) {
      const currentEchelon = m.offGrid
        ? (m.offGridReferenceEchelon ?? m.echelon)
        : m.echelon
      addMarker(
        byClass,
        m.class_id,
        currentEchelon,
        m.offGrid ? { kind: 'current', offGrid: true } : { kind: 'current' },
      )
    }

    // Scénario (peut viser une autre classe).
    const change = scenarioChanges.find((c) => !c.virtual && c.email === m.email)
    const scenarioClass = change?.to_class_id ?? null
    if (change && scenarioClass) {
      addMarker(byClass, scenarioClass, change.to_echelon, { kind: 'scenario' })
    }

    for (const [classId, cells] of byClass) {
      push({
        id: m.email,
        name: m.name,
        email: m.email,
        virtual: false,
        class_id: classId,
        isCurrentClass: classId === m.class_id,
        hasScenario: classId === scenarioClass,
        niveau: classId === m.class_id ? (m.niveau ?? null) : null,
        active: m.active !== false,
        offGrid: classId === m.class_id ? (m.offGrid ?? false) : false,
        markers: Object.fromEntries(cells),
      })
    }
  }

  // Employés fictifs : une ligne dans leur classe cible.
  for (const [i, c] of scenarioChanges.entries()) {
    if (!c.virtual || !c.to_class_id) continue
    push({
      id: c.local_id ?? `virtuel-${i}`,
      name: c.name,
      email: null,
      virtual: true,
      class_id: c.to_class_id,
      isCurrentClass: true,
      hasScenario: true,
      niveau: null,
      active: true,
      offGrid: false,
      markers: { [c.to_echelon]: [{ kind: 'scenario' }] },
    })
  }

  // Dans chaque classe : lignes courantes d'abord (ordre alpha conservé),
  // puis lignes d'autres classes (repliables), puis fictifs.
  for (const lines of Object.values(result)) {
    lines.sort((a, b) => {
      const rank = (l: EmployeeGridLine) => (l.virtual ? 2 : l.isCurrentClass ? 0 : 1)
      return rank(a) - rank(b)
    })
  }

  return result
}
